import type { ComponentType } from 'react';

import { ConnectedWorld } from '@/components/trial/lp/connected-world';
import DefaultCustomTrialDiscord from '@/components/trial/lp/default-custom-trial-discord';
import { FastStart } from '@/components/trial/lp/fast-start';
import { GoToTheTrial } from '@/components/trial/lp/go-to-the-trial';
import { Matrix } from '@/components/trial/lp/matrix';
import { PingProof } from '@/components/trial/lp/ping-proof';
import { ProofOnScreen } from '@/components/trial/lp/proof-on-screen';
import { TourBoost } from '@/components/trial/lp/tour-boost';
import { TrialBeta } from '@/components/trial/lp/trial-beta';
import { ProChampions } from '@/components/trial/lp/v1a';
import { PingDrop } from '@/components/trial/lp/v1b';
import { EveryoneUses } from '@/components/trial/lp/v1c';
import { LagIsDead } from '@/components/trial/lp/v2a';
import { PingTester } from '@/components/trial/lp/v2b';
import { LivePing } from '@/components/trial/lp/v2c';
import { CodeRain } from '@/components/trial/lp/v3b';
import { GreenPill } from '@/components/trial/lp/v3c';
import { Arena } from '@/components/trial/lp/v4a';
import { EnterTheArena } from '@/components/trial/lp/v4b';
import { StarWars } from '@/components/trial/lp/v4c';
import { JoinTheRebellion } from '@/components/trial/lp/v4d';
import { DigitalFortress } from '@/components/trial/lp/v5a';
import type { DefaultTrialProps } from '@/components/trial/trial-layout-switcher';

import { TRIAL_CONNECTED_WORLD } from './connected-world';
import { TRIAL_DEFAULT } from './default';
import { TRIAL_PAGES_DISCORD } from './discord';
import { TRIAL_PAGES_FAST_START } from './fast-start';
import { TRIAL_PAGES_GO_TO_THE_TRIAL } from './go-to-the-trial';
import { TRIAL_PAGES_MATRIX } from './matrix';
import { TRIAL_PAGES_PING_PROOF } from './ping-proof';
import { TRIAL_PAGES_PROOF_ON_SCREEN } from './proof-on-screen';
import { TRIAL_PAGES_TOUR_BOOST } from './tour-boost';
import { TRIAL_PAGES_TRIAL_BETA } from './trial-beta';
import { TRIAL_PAGES_V1A } from './v1a';
import { TRIAL_PAGES_V1B } from './v1b';
import { TRIAL_PAGES_V1C } from './v1c';
import { TRIAL_PAGES_V2A } from './v2a';
import { TRIAL_PAGES_V2B } from './v2b';
import { TRIAL_PAGES_V2C } from './v2c';
import { TRIAL_PAGES_V3B } from './v3b';
import { TRIAL_PAGES_V3C } from './v3c';
import { TRIAL_PAGES_V4A } from './v4a';
import { TRIAL_PAGES_V4B } from './v4b';
import { TRIAL_PAGES_V4C } from './v4c';
import { TRIAL_PAGES_V4D } from './v4d';
import { TRIAL_PAGES_V5A } from './v5a';

export const TRIAL_PAGES_COMPONENTS: Record<string, ComponentType<DefaultTrialProps>> = {
  // Sentinel used by /preview/[trial] when the URL carries no `key` — not a real API key, so it is
  // excluded from KNOWN_TRIAL_PAGE_KEYS below.
  [TRIAL_DEFAULT]: Matrix,
  [TRIAL_PAGES_MATRIX]: Matrix,
  [TRIAL_PAGES_TOUR_BOOST]: TourBoost,
  [TRIAL_CONNECTED_WORLD]: ConnectedWorld,
  [TRIAL_PAGES_FAST_START]: FastStart,
  [TRIAL_PAGES_PING_PROOF]: PingProof,
  [TRIAL_PAGES_DISCORD]: DefaultCustomTrialDiscord,
  [TRIAL_PAGES_GO_TO_THE_TRIAL]: GoToTheTrial,
  [TRIAL_PAGES_PROOF_ON_SCREEN]: ProofOnScreen,
  [TRIAL_PAGES_TRIAL_BETA]: TrialBeta,
  [TRIAL_PAGES_V3C]: GreenPill,
  [TRIAL_PAGES_V3B]: CodeRain,
  [TRIAL_PAGES_V1A]: ProChampions,
  [TRIAL_PAGES_V1B]: PingDrop,
  [TRIAL_PAGES_V1C]: EveryoneUses,
  [TRIAL_PAGES_V2A]: LagIsDead,
  [TRIAL_PAGES_V2C]: LivePing,
  [TRIAL_PAGES_V2B]: PingTester,
  [TRIAL_PAGES_V4D]: JoinTheRebellion,
  [TRIAL_PAGES_V4A]: Arena,
  [TRIAL_PAGES_V4C]: StarWars,
  [TRIAL_PAGES_V4B]: EnterTheArena,
  [TRIAL_PAGES_V5A]: DigitalFortress,
};

// Registry keys minus the TRIAL_DEFAULT sentinel — the set of keys the API can legitimately draw and
// expect this repo to render. Feeds resolveTrialRender's `knownKeys` input directly: an API-drawn key
// of literally `TRIAL_DEFAULT` must resolve to `unavailable`, not to Matrix (see the sentinel test in
// src/__tests__/pages/trial-url-destination.test.tsx).
export const KNOWN_TRIAL_PAGE_KEYS = new Set(Object.keys(TRIAL_PAGES_COMPONENTS).filter((key) => key !== TRIAL_DEFAULT));
